import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightSvg } from '~/svg';
import { fetchAppliedGrants } from '~/services/grants';
import { IGrant } from '~/types/campaigns';

// The most recently applied grants, each campaign linking to its record
// (HHLTB-2544).
const GRANT_RECIPIENTS_LIMIT = 5;

const formatAmount = (value: string) => {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const GrantRecipients = () => {
    const [grants, setGrants] = useState<IGrant[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await fetchAppliedGrants({
                    limit: GRANT_RECIPIENTS_LIMIT,
                });
                if (!cancelled) setGrants(resp);
            } catch (error) {
                console.error('Error fetching grant recipients:', error);
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className='mb-4 h-full rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark md:p-6 xl:p-7.5'>
            <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <h2 className='text-xl font-semibold text-black dark:text-white'>
                    Grant Recipients
                </h2>

                <Link
                    to='/applied-grants'
                    className='flex items-center gap-2 text-primary'
                >
                    View All <ArrowRightSvg />
                </Link>
            </div>

            {loading ? (
                <div className='flex items-center justify-center py-8'>
                    <p className='text-secondary'>Loading...</p>
                </div>
            ) : failed ? (
                <div className='flex items-center justify-center py-8'>
                    <p className='text-secondary'>
                        Could not load grant recipients
                    </p>
                </div>
            ) : grants.length === 0 ? (
                <div className='flex items-center justify-center py-8'>
                    <p className='text-secondary'>No grants applied yet</p>
                </div>
            ) : (
                <div className='flex flex-col gap-2'>
                    <div className='grid grid-cols-12 py-2'>
                        <div className='col-span-5'>
                            <p className='text-sm text-secondary'>Campaign</p>
                        </div>
                        <div className='col-span-4'>
                            <p className='text-sm text-secondary'>Grant</p>
                        </div>
                        <div className='col-span-3'>
                            <p className='text-right text-sm text-secondary'>
                                Amount
                            </p>
                        </div>
                    </div>
                    {grants.map((grant) => (
                        <div
                            key={grant.id}
                            className='relative z-1 grid grid-cols-12 py-2'
                        >
                            <div className='col-span-5'>
                                {grant.campaign_id ? (
                                    <Link
                                        to={`/campaigns/${grant.campaign_id}`}
                                        className='text-primary hover:underline'
                                    >
                                        {grant.campaign}
                                    </Link>
                                ) : (
                                    // No campaign matches the grant's NTAF ID
                                    <p>{grant.ntaf_id}</p>
                                )}
                            </div>
                            <div className='col-span-4'>
                                <p>{grant.name}</p>
                            </div>
                            <div className='col-span-3'>
                                <p className='text-right'>
                                    {formatAmount(grant.amount)}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default GrantRecipients;
